import React, { useContext, useMemo } from 'react'

import FormatCurrency from 'components/Common/FormatCurrency'
import Pill from 'components/Luxkit/Pill'

import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import Caption from 'components/Luxkit/Typography/Caption'
import GeoContext from 'contexts/geoContext'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import WalledContent from 'components/Common/WalledContent'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import LuxuryPlusPriceGuard from 'loyalty/components/LuxuryPlusPriceGuard'
import LuxuryPlusPrice from 'loyalty/components/LuxuryPlusPrice'

interface Props {
  offer: App.Offer | App.OfferSummary;
}

function LEOfferPriceDetails(props: Props) {
  const {
    offer,
  } = props

  const pkg = offer.lowestPricePackage!
  const { isTaxExclusiveRegion } = useContext(GeoContext)
  const propertyFees = pkg.propertyFees ?? 0

  const totalPrice = isTaxExclusiveRegion ? pkg.price : pkg.price + propertyFees
  const totalMemberPrice = isTaxExclusiveRegion ? pkg.memberPrice : pkg.memberPrice + propertyFees
  const totalValue = isTaxExclusiveRegion ? pkg.value : pkg.value + propertyFees

  const valueOffPercent = getValueOffPercent(totalValue, totalPrice)
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const totalMemberPriceOrTotalPrice = totalMemberPrice || totalPrice

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalValue > totalMemberPriceOrTotalPrice && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = useMemo(() => {
    if (offer.tileDurationLabel) {
      return `${offer.tileDurationLabel.toLocaleLowerCase()} from`
    }
    if (LPCRegionLOS) {
      return `${pkg.duration} nights from`
    }
    return 'From'
  }, [LPCRegionLOS, offer.tileDurationLabel, pkg.duration])

  const wall = <OfferTileLoggedInButtons
    signUpText="Sign up for free"
    align="right"
    fullWidth={false}
    discountLabelAlign="end"
  />

  return (
    <WalledContent enforced={offer.walledGarden} wall={wall}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
        <Group direction="vertical">
          <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
          <LuxuryPlusPriceGuard
            showMemberPrice={pkg.memberPrice > 0}
            fallbackPrice={<>
              <PriceRowPrice
                size="L"
                price={totalPrice}
                saleUnit={offer.saleUnit}
                taxesAndFees={pkg.taxesAndFees}
              />
              <PriceRowTaxesAndFees
                isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
                taxesAndFees={pkg.taxesAndFees}
              />
            </>}
          >
            <LuxuryPlusPrice
              memberPrice={totalMemberPrice}
              saleUnit={offer.saleUnit}
              taxesAndFees={pkg.taxesAndFees}
              price={totalPrice}
              size="L"
            />
          </LuxuryPlusPriceGuard>
          {shouldShowValue && (
            <ValueWrapper variant="large" weight="bold" colour="neutral-one">
              Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
              {shouldShowRate &&
                <PaletteProvider type="default">
                  <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
                </PaletteProvider>
              }
            </ValueWrapper>
          )}
          {staysFrom && showStaysFromLabel &&
            <Caption colour="neutral-three" variant="medium">
              Stays from {pluralizeToString('night', staysFrom)} available
            </Caption>
        }
          {!!pkg.roomOccupancy && offer.type === 'tour' && (
            <Caption colour="neutral-two" variant="medium">
              {pkg.roomOccupancy}
            </Caption>
          )}
        </Group>
        <CSSBreakpoint min="desktop">
          <TextButton kind="primary" nonInteractable>
            View
          </TextButton>
        </CSSBreakpoint>
      </Group>
    </WalledContent>

  )
}

export default LEOfferPriceDetails
